/**
 * CanvasCard — one whiteboard card = one merged commit-group (1..N
 * paragraphs). Pure presentational: position/size arrive resolved
 * (morph-lerped) from CanvasView; interactions call back up.
 */

import { useEffect, useRef } from 'react'
import { parseMarkdown } from '../utils/markdownConfig'
import { beginDrag, beginResize } from './cardInteractions'
import type { SetCardOverrides } from './cardInteractions'
import type { MergedBlock, CanvasBlock } from './mergeBlocks'
import { CARD_H_MIN, CARD_H_MAX } from './layouts'

interface Props {
  block: CanvasBlock
  /** Merged commit-group this card renders; undefined → single block. */
  merged: MergedBlock | undefined
  /** textBlockId of the block — DOM identity + selection handle. */
  id: string
  /** Stable React key survives the block_id arrival (used by parent). */
  x: number
  y: number
  width: number
  /** Numeric height while morphing; undefined → content-driven. */
  height: number | undefined
  /** Explicit user-resized height, if any. */
  localHeight: number | undefined
  opacity: number
  /** Suppress the FLIP transition (drag in progress / morph active). */
  suppressTransition: boolean
  selected: boolean
  editing: boolean
  tension: boolean
  onSelect: (id: string) => void
  onEditToggle: (block: CanvasBlock, id: string, editing: boolean) => void
  onDelete: (merged: MergedBlock | undefined, block: CanvasBlock, id: string) => void
  onImageDrop: (block: CanvasBlock, file: File) => void
  setOverrides: SetCardOverrides
  /** Reports the real rendered card height so layouts can replace the
   *  char-count estimate (which markdown syntax inflates badly). */
  onMeasure?: (id: string, height: number) => void
}

export function CanvasCard({
  block: b,
  merged,
  id,
  x,
  y,
  width,
  height,
  localHeight,
  opacity,
  suppressTransition,
  selected,
  editing,
  tension,
  onSelect,
  onEditToggle,
  onDelete,
  onImageDrop,
  setOverrides,
  onMeasure,
}: Props) {
  const paragraphs = merged?.paragraphs ?? [b]

  // Measure the real rendered height and report it upward. Only while
  // the card is content-driven (no explicit morph height) — mid-morph
  // the DOM height is an interpolated value, not the layout truth.
  const elRef = useRef<HTMLDivElement | null>(null)
  const contentDriven = height == null
  useEffect(() => {
    const el = elRef.current
    if (!el || !contentDriven || !onMeasure) return
    const report = () => onMeasure(id, el.offsetHeight)
    report()
    const ro = new ResizeObserver(report)
    ro.observe(el)
    return () => ro.disconnect()
  }, [id, contentDriven, onMeasure])

  return (
    <div
      ref={(el) => {
        elRef.current = el
        // First-mount gate: data-mounted lands one frame after the
        // initial paint, so a freshly-keyed card never glides in from
        // (0,0) — CSS suppresses transitions until the attr exists.
        if (el && !el.dataset.mounted) {
          requestAnimationFrame(() => {
            el.dataset.mounted = 'true'
          })
        }
      }}
      className="canvas-card"
      data-block-id={id}
      data-block-type={b.block_type}
      data-selected={selected}
      data-editing={editing}
      data-tension={tension}
      onClick={() => !editing && onSelect(id)}
      onMouseDown={(e) => {
        if (editing) return
        if ((e.target as HTMLElement).closest('.canvas-card__resize')) return
        beginDrag(e, id, { x, y }, setOverrides)
      }}
      onDragOver={(e) => {
        if (
          e.dataTransfer.types.includes('Files') ||
          e.dataTransfer.types.includes('application/x-moz-file')
        ) {
          e.preventDefault()
          e.dataTransfer.dropEffect = 'copy'
          e.currentTarget.setAttribute('data-image-dragover', 'true')
        }
      }}
      onDragLeave={(e) => {
        e.currentTarget.removeAttribute('data-image-dragover')
      }}
      onDrop={(e) => {
        e.preventDefault()
        e.currentTarget.removeAttribute('data-image-dragover')
        const file = e.dataTransfer.files[0]
        if (file && file.type.startsWith('image/')) {
          onImageDrop(b, file)
        }
      }}
      style={{
        position: 'absolute',
        left: x,
        top: y,
        width,
        ...(height != null
          ? { height }
          : localHeight != null
            ? { height: localHeight }
            : { maxHeight: CARD_H_MAX }),
        opacity,
        // FLIP-glide for layout-induced shifts (CRDT mid-inserts move
        // every rightward card). Off while dragging (1:1 mouse) and
        // while the Doc↔Canvas morph owns the animation.
        transition: suppressTransition
          ? 'none'
          : 'left 280ms cubic-bezier(0.4, 0.0, 0.2, 1), top 280ms cubic-bezier(0.4, 0.0, 0.2, 1)',
      }}
    >
      <div className="canvas-card__author" style={{ background: b.author.color }} />
      <div className="canvas-card__body">
        <div className="canvas-card__chrome">
          <span className="canvas-card__author-name" title={b.author.name}>
            {b.author.name.split(/\s+/)[0]}
          </span>
          {paragraphs.length > 1 && (
            <span
              className="canvas-card__paragraph-count"
              title={`${paragraphs.length} Absätze in diesem Commit`}
            >
              × {paragraphs.length}
            </span>
          )}
          <div className="canvas-card__btn-row">
            <button
              type="button"
              className="canvas-card__edit-btn"
              title={
                editing
                  ? 'Speichern und schließen (Enter / Esc / blur)'
                  : 'Diese Kachel bearbeiten'
              }
              onClick={(e) => {
                e.stopPropagation()
                onEditToggle(b, id, editing)
              }}
            >
              {editing ? 'Read' : 'Edit'}
            </button>
            <button
              type="button"
              className="canvas-card__delete-btn"
              title={
                paragraphs.length > 1
                  ? `Alle ${paragraphs.length} Absätze dieses Commits löschen`
                  : 'Diese Kachel löschen (oder Delete-Taste wenn markiert)'
              }
              onClick={(e) => {
                e.stopPropagation()
                onDelete(merged, b, id)
              }}
              aria-label="Kachel löschen"
            >
              ✕
            </button>
          </div>
        </div>
        {/* One commit = one card. Multi-paragraph commits stack their
          * paragraphs with hairline separators; single paragraphs render
          * flat. Markdown via parseMarkdown (bold, code, links, lists). */}
        {paragraphs.length === 1 ? (
          <div
            className="canvas-card__text"
            dangerouslySetInnerHTML={{ __html: parseMarkdown(b.text_preview) }}
          />
        ) : (
          <div className="canvas-card__paragraphs">
            {paragraphs.map((p, idx) => (
              <div
                key={p.block_id ?? `p_${p.paragraph_index}_${idx}`}
                className="canvas-card__paragraph"
                dangerouslySetInnerHTML={{ __html: parseMarkdown(p.text_preview) }}
              />
            ))}
          </div>
        )}
      </div>
      {/* Resize handle — local-only override; CRDT write-back is the
        * Phase 5c follow-up. */}
      <div
        className="canvas-card__resize"
        title="Größe ändern"
        onMouseDown={(e) =>
          beginResize(
            e,
            id,
            { width, height: height ?? localHeight ?? CARD_H_MIN },
            setOverrides,
          )
        }
      />
    </div>
  )
}
